/**
 * **How your site looks** — FR-I11, T-4.13; redrawn for C13 (spec §10.5 item 4,
 * ADR-0032, Wave 14B — T-4.62). A settings page, not a code editor (dukaan §2,
 * and ADR-0013 §4 is why).
 *
 * The owner, 2026-09-25: *"we do not required right side preview, required
 * button like See it in your draft of Navigation"*, *"in proper card view like
 * Settings"*, and *"UI of Navigation … we want in same manner"*. So:
 *
 * - **No inline preview.** The iframe that sat beside the panel since Wave 4D
 *   is gone; the edit bar's *See it in your draft ↗* opens the firm's own
 *   standing preview link (C6, ADR-0024) in a new tab, as Navigation's does.
 * - **Four cards in Navigation's manner** — an in-body heading and one muted
 *   line, choices drawn as selectable cards rather than selects:
 *   1. *Colours* — primary and accent, each with a swatch, a colour picker and
 *      its hex, the template's own value shown beside it, and the contrast note.
 *   2. *Typeface* — each face set **in itself**, from the same self-hosted files
 *      the public site uses (`site/fonts.ts`, served on this host under
 *      `/site-assets/fonts/`), so what the firm picks is what it saw.
 *   3. *Header & footer layout* — each option a card with a small drawn diagram.
 *   4. *Logo & browser icon* — the firm's one logo (ADR-0030), which **saves
 *      itself** through `LogoControl` exactly as *Your firm* draws it, and the
 *      favicon, which saves with the rest.
 * - **One Save** — the edit bar at the bottom sends the theme and the favicon
 *   together; Discard puts both back; leaving with either unsaved asks first.
 *
 * **C17 (Wave 18D, T-4.73, ADR-0036 §5–§7)** adds three things, all saved
 * by the same one Save:
 *
 * - **Preset swatches**, where the template ships presets (Encore's four). A
 *   preset is a shortcut and **not a setting**: choosing one writes `primary`
 *   and `accent` — the two hex values a firm can already type — and nothing
 *   else (`applyPreset`).
 * - **A heading-face picker**, drawn only where the template offers a choice
 *   (`Template.displayChoices`); it writes `theme.display`.
 * - **One page / Pages** — `theme.nav_mode`. Under *One page* the header is
 *   the home page's labelled sections; the firm's own menu is kept, not
 *   deleted, and comes back with *Pages*.
 *
 * The two new body faces (DM Sans, Space Grotesk) need nothing here: the
 * typeface card is drawn from the vocabulary's `fonts`, which now has seven.
 *
 * The presets and the display choices live on the **web** half's template
 * row (`site/templates`), not the service's, so they are read with a dynamic
 * import — its own chunk, loaded by this page alone, rather than every
 * theme's stylesheet in the staff bundle.
 *
 * Seven theme fields, and they are the seven the renderer reads: `primary`,
 * `accent`, `font`, `header_layout`, `footer_layout`, `display`, `nav_mode`.
 * `theme` holds **only the
 * firm's own choices**; what the site renders is those over the template's
 * (`resolved_theme`). The difference is what makes *Back to the template's* a
 * real action and not a no-op, so the screen always says which is which.
 *
 * Every list on this screen comes from `GET /api/v1/site/vocabulary`, so the
 * fonts a firm is offered are the fonts `validate_theme` will accept **and**
 * the fonts whose files ship in the image (`tests/site-vocabulary.test.ts`).
 *
 * ## Colours
 *
 * Two, not a dozen. `site/theme.ts` derives the tints, the shades and **a
 * foreground chosen by contrast**, so a firm that picks pale yellow gets black
 * text on its buttons without having to know why. `contrast.ts` is that same
 * computation, shown before the firm commits rather than discovered on the
 * published page.
 */

import { Alert, Badge, Button, Card, CardBody, Input, Skeleton } from '@fiveox/ui'
import { Check, RotateCcw } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { EditBar, EditPage, useDraftPreviewHref, useLeaveGuard } from '../../components/edit'
import { LogoControl } from '../../components/LogoControl'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { site, type Theme } from '../../lib/api/site'
import { DISPLAY_FONTS, FONTS, fontFaceCss, fontStack } from '../../../site/fonts'
import type { ThemePreset } from '../../../site/templates'
import { asColourInput, contrastNote, inkOn, isStorableColour } from './contrast'
import { ImageField } from './fields/ImageField'
import { useSite } from './useSite'

/** The seven keys, in the order the page draws them. */
const THEME_KEYS = ['primary', 'accent', 'font', 'header_layout', 'footer_layout', 'display', 'nav_mode'] as const

/** The heading faces, by key — for the C17 picker. */
const DISPLAY_BY_KEY = new Map(DISPLAY_FONTS.map((f) => [f.key, f]))

/** FR-I18's two ways round a site, in the firm's words. */
export const NAV_MODES: Record<string, { label: string; note: string }> = {
  pages: { label: 'Pages', note: 'Your header links your pages — the menu you set in Navigation.' },
  sections: {
    label: 'One page',
    note: 'Your header jumps to the sections of your home page that have a menu label. Your own menu is kept for when you switch back.',
  },
}

/**
 * **A preset is a shortcut, not a setting** (ADR-0036 §5): it writes the same
 * two hex values a firm can already type — `primary` and `accent` — and
 * nothing else.
 */
export function applyPreset(theme: Partial<Theme>, preset: Pick<ThemePreset, 'primary' | 'accent'>): Partial<Theme> {
  return { ...theme, primary: preset.primary, accent: preset.accent }
}

/** Whether the colours the site renders are exactly a preset's. */
export function presetChosen(resolved: Pick<Theme, 'primary' | 'accent'>, preset: ThemePreset): boolean {
  return (
    (resolved.primary ?? '').toLowerCase() === preset.primary.toLowerCase() &&
    (resolved.accent ?? '').toLowerCase() === preset.accent.toLowerCase()
  )
}

/** What a template offers beyond the five (C17, I-2): presets and heading
 *  faces; and since C21 (ADR-0043 §6.1) the body faces it offers — empty for
 *  a template that declares none, which offers all of `FONTS`. */
export interface TemplateChoices {
  presets: ThemePreset[]
  displayChoices: string[]
  bodyChoices: string[]
}

/**
 * **The typefaces the panel offers** (C21, ADR-0043 §6.1, T-4.90): the
 * template's `bodyChoices` when it declares them, else every face the service
 * accepts (`vocabulary.fonts`), as before C21. Either way held to the
 * service's list, so a face `validate_theme` would refuse is never offered.
 *
 * The firm's **current** face is kept on the list when a theme's choices
 * leave it out — a font saved before the firm switched theme, say — so the
 * group always shows what the site is set in; it is never added otherwise.
 */
export function bodyFontsOffered(
  serviceFonts: readonly string[],
  bodyChoices: readonly string[] | null | undefined,
  current?: string | null,
): string[] {
  if (!bodyChoices || bodyChoices.length === 0) return [...serviceFonts]
  const accepted = new Set(serviceFonts)
  const offered = bodyChoices.filter((key) => accepted.has(key))
  if (current && accepted.has(current) && !offered.includes(current)) offered.push(current)
  return offered
}

/**
 * The template's presets and display choices, from the web half's own rows —
 * loaded on demand, so every theme's stylesheet is not in the staff bundle.
 * `null` until it has loaded; empty lists for a template that offers neither.
 */
export function useTemplateChoices(code: string | undefined): TemplateChoices | null {
  const [choices, setChoices] = useState<TemplateChoices | null>(null)
  useEffect(() => {
    if (!code) return
    let live = true
    import('../../../site/templates').then(
      (m) => {
        const row = m.TEMPLATES.find((t) => t.code === code)
        if (live)
          setChoices({
            presets: row?.presets ?? [],
            displayChoices: row?.displayChoices ?? [],
            bodyChoices: row?.bodyChoices ?? [],
          })
      },
      () => {
        // A chunk that did not load offers nothing extra — the five still work.
        if (live) setChoices({ presets: [], displayChoices: [], bodyChoices: [] })
      },
    )
    return () => {
      live = false
    }
  }, [code])
  return choices
}

/** What each curated font is for, in one line — `site/fonts.ts`'s own `trait`
 *  when the family is there, so the panel and the renderer name one set. An
 *  unknown key is offered under its own name rather than hidden. */
const FONT_BY_KEY = new Map(FONTS.map((f) => [f.key, f]))

/**
 * **The layouts' words live with the drawing now** — C24 item 13 (ADR-0046
 * §4). `LAYOUT_WORDS` and `LayoutDiagram` moved to `components/site/`, so
 * this screen and *Navigation* draw the same twelve from one place.
 *
 * Kept here as a re-export because this screen's tests and
 * `tests/site-look.test.tsx` name it, and because a reader of this file should
 * not have to know where it went.
 */
import { LAYOUT_WORDS, LayoutDiagram, layoutWords } from '../../components/site/LayoutDiagram'

export { LAYOUT_WORDS, LayoutDiagram, layoutWords }

/** The body the edit bar's Save sends — the whole of the page's one write. */
export function themeSaveBody(theme: Partial<Theme>, favicon: string) {
  return { theme: strip(theme), favicon_url: favicon.trim() }
}

/** Why Save would be refused before the request, or `null`. */
export function themeProblem(theme: Partial<Theme>): string | null {
  for (const key of ['primary', 'accent'] as const) {
    const value = theme[key]
    if (value && !isStorableColour(value)) {
      return `Your ${key} colour must look like #1f3d63 — a hash and six characters.`
    }
  }
  return null
}

export function SiteTheme() {
  const { can } = useEvaluation()
  const editable = can(SITE, 'can_update')
  const { vocabulary, site: current, setSite } = useSite()
  const previewHref = useDraftPreviewHref()
  const choices = useTemplateChoices(current?.template)

  const [theme, setTheme] = useState<Partial<Theme>>({})
  /** The firm's one logo (ADR-0030). Saved by the control itself, so it is
   *  never part of `dirty` and a logo change never discards a theme edit. */
  const [logo, setLogo] = useState<string | null>(null)
  const [favicon, setFavicon] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!current) return
    setTheme(current.theme ?? {})
    setLogo(current.logo_url ?? null)
    setFavicon(current.favicon_url ?? '')
  }, [current])

  /** The template's own values — what *Back to the template's* returns to. */
  const defaults: Theme = useMemo(() => {
    const own = vocabulary?.templates.find((t) => t.code === current?.template)?.theme
    // A template the vocabulary does not list (a retired code) still has its
    // values in `resolved_theme` wherever the firm has not chosen.
    return { ...(current?.resolved_theme ?? ({} as Theme)), ...(own ?? {}) }
  }, [vocabulary, current])

  /** What the site will actually look like: the firm's choices over the
   *  template's, exactly as `resolve_theme` composes them. */
  const resolved: Theme = useMemo(() => ({ ...defaults, ...strip(theme) }), [defaults, theme])

  const dirty =
    JSON.stringify(strip(theme)) !== JSON.stringify(strip(current?.theme ?? {})) ||
    favicon.trim() !== (current?.favicon_url ?? '')

  const guard = useLeaveGuard(dirty)
  const problem = themeProblem(theme)

  const discard = () => {
    if (!current) return
    setTheme(current.theme ?? {})
    setFavicon(current.favicon_url ?? '')
    setError(null)
    setNotice(null)
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const body = await site.update(themeSaveBody(theme, favicon))
      setSite(body.data)
      setNotice('Saved to your draft. See it in your draft now; your live site changes when you publish.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those settings could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  // w13a — through `PATCH /api/v1/site`, which writes the profile's column
  // under `site:can_update`. Not `setSite`: that would reload the page from
  // the server and drop a colour the firm has not saved yet.
  const saveLogo = async (next: string | null) => {
    const body = await site.update({ logo_url: next ?? '' })
    setLogo(body.data.logo_url)
  }

  if (!vocabulary || !current) {
    return (
      <div className="space-y-4" aria-label="Loading your theme">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const set = (patch: Partial<Theme>) => {
    setTheme({ ...theme, ...patch })
    setNotice(null)
  }
  const unset = (key: keyof Theme) => {
    const next = { ...theme }
    delete next[key]
    setTheme(next)
    setNotice(null)
  }
  const ownChoices = Object.keys(strip(theme)).length
  const contrast = contrastNote(resolved.primary, resolved.accent)
  const firmName = current.name?.trim() || 'Your firm'
  // C21: the template's own body faces, or all of them (ADR-0043 §6.1).
  const bodyOffered = bodyFontsOffered(vocabulary.fonts, choices?.bodyChoices, resolved.font)
  const offered = bodyOffered.map((key) => FONT_BY_KEY.get(key)).filter((f) => f !== undefined)
  // C17: the heading faces this template offers — its own list, held to the
  // service's, so a face the service would refuse is never offered.
  const serviceDisplay = new Set(vocabulary.display_choices ?? [])
  const displayOffered = (choices?.displayChoices ?? [])
    .filter((key) => serviceDisplay.has(key))
    .map((key) => DISPLAY_BY_KEY.get(key))
    .filter((f) => f !== undefined)
  const presets = choices?.presets ?? []
  const navModes = (vocabulary.nav_modes ?? []).filter((mode) => mode in NAV_MODES)

  return (
    <EditPage
      title="How your site looks"
      back={{ to: '/site', label: 'Your website' }}
      description="Two colours, a typeface and where your name sits. The tints, the hovers and the text on your buttons are worked out from these, so you don’t have to pick a dozen. Changes go to your draft — your live site changes when you publish."
      actions={
        editable ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || ownChoices === 0}
            title={ownChoices === 0 ? 'You are already using your template’s look' : undefined}
            onClick={() => setTheme({})}
          >
            <RotateCcw size={14} aria-hidden /> Back to the template’s
          </Button>
        ) : undefined
      }
    >
      {guard}
      {/* The faces, from this host — the same files the public site serves, so
          each card below is set in the face it offers. */}
      <style>{[...offered, ...displayOffered].map(fontFaceCss).join('')}</style>

      {error && <Alert tone="error">{error}</Alert>}
      {notice && !dirty && <Alert tone="success">{notice}</Alert>}

      {/* ── 1. colours ───────────────────────────────────────────────── */}
      <Card data-look-card="colours">
        <CardBody className="space-y-4">
          <CardHeading
            title="Colours"
            note="Your primary colour is for headings, buttons and links; the accent for highlights and the second button."
          />
          {presets.length > 0 && (
            <div className="space-y-2" data-presets="">
              <p className="text-sm font-medium text-foreground">Your template’s pairs</p>
              <div role="radiogroup" aria-label="Colour pairs" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {presets.map((preset) => {
                  const chosen = presetChosen(resolved, preset)
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      data-preset={preset.key}
                      disabled={!editable}
                      onClick={() => {
                        setTheme(applyPreset(theme, preset))
                        setNotice(null)
                      }}
                      className={`flex min-w-0 items-center gap-2 rounded-xl border p-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        chosen ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-surface-raised hover:border-primary/40'
                      } ${editable ? '' : 'cursor-not-allowed opacity-70'}`}
                    >
                      <span className="flex shrink-0 overflow-hidden rounded-md ring-1 ring-border" aria-hidden>
                        <span className="block size-6" style={{ backgroundColor: preset.primary }} />
                        <span className="block size-6" style={{ backgroundColor: preset.accent }} />
                      </span>
                      <span className="truncate font-medium text-foreground">{preset.name}</span>
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                A pair fills in the two colours below — nothing else. You can still change either.
              </p>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <ColourPanel
              name="primary"
              label="Primary"
              value={theme.primary ?? ''}
              resolved={resolved.primary}
              template={defaults.primary}
              disabled={!editable}
              onChange={(next) => set({ primary: next })}
              onReset={() => unset('primary')}
            />
            <ColourPanel
              name="accent"
              label="Accent"
              value={theme.accent ?? ''}
              resolved={resolved.accent}
              template={defaults.accent}
              disabled={!editable}
              onChange={(next) => set({ accent: next })}
              onReset={() => unset('accent')}
            />
          </div>
          {/* Shown before the firm commits, not discovered afterwards on a
              published page. */}
          <Alert tone={contrast.tone}>
            <span data-contrast-note="">{contrast.message}</span>
          </Alert>
        </CardBody>
      </Card>

      {/* ── 2. typeface ──────────────────────────────────────────────── */}
      <Card data-look-card="typeface">
        <CardBody className="space-y-4">
          <CardHeading
            title="Typeface"
            note="Every word on your site is set in it. Each one here is shown in itself."
          />
          <div role="radiogroup" aria-label="Typeface" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {bodyOffered.map((key) => {
              const font = FONT_BY_KEY.get(key)
              return (
                <ChoiceCard
                  key={key}
                  kind="font"
                  value={key}
                  checked={resolved.font === key}
                  isTemplate={defaults.font === key}
                  disabled={!editable}
                  onSelect={() => set({ font: key })}
                >
                  <span
                    className="block truncate text-2xl leading-tight text-foreground"
                    style={font ? { fontFamily: fontStack(font) } : undefined}
                  >
                    {firmName}
                  </span>
                  <span
                    className="mt-1 block text-sm text-foreground/80"
                    style={font ? { fontFamily: fontStack(font) } : undefined}
                  >
                    Returns filed, books kept, advice given — 0123456789
                  </span>
                  <span className="mt-3 block text-sm font-medium text-foreground">
                    {font?.label ?? sentence(key)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {font?.trait ?? 'A curated, self-hosted family.'}
                  </span>
                </ChoiceCard>
              )
            })}
          </div>
          {/* The reason there are a handful and not four hundred. */}
          <p className="text-xs text-muted-foreground">
            Every one of these ships with your site. Your pages never fetch a font from somewhere
            else, so nobody outside your firm learns who visited.
          </p>
        </CardBody>
      </Card>

      {/* ── 2b. heading face (C17) — only where the template offers one ── */}
      {displayOffered.length > 0 && (
        <Card data-look-card="display">
          <CardBody className="space-y-4">
            <CardHeading
              title="Heading face"
              note="Your template lets you choose the face its headings are set in. Your typeface above is for everything else."
            />
            <div role="radiogroup" aria-label="Heading face" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {displayOffered.map((font) => (
                <ChoiceCard
                  key={font.key}
                  kind="display"
                  value={font.key}
                  checked={resolved.display === font.key}
                  isTemplate={defaults.display === font.key}
                  disabled={!editable}
                  onSelect={() => set({ display: font.key })}
                >
                  <span className="block truncate text-2xl leading-tight text-foreground" style={{ fontFamily: fontStack(font) }}>
                    {firmName}
                  </span>
                  <span className="mt-3 block text-sm font-medium text-foreground">{font.label}</span>
                  <span className="block text-xs text-muted-foreground">{font.trait}</span>
                </ChoiceCard>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ── 3. header & footer layout ────────────────────────────────── */}
      <Card data-look-card="layout">
        <CardBody className="space-y-5">
          <CardHeading
            title="Header & footer layout"
            note="Where your name and menu sit at the top of every page, and how the bottom is laid out. Your menu’s items are set in Navigation."
          />
          <LayoutGroup
            label="Header"
            kind="header"
            options={vocabulary.header_layouts}
            value={resolved.header_layout}
            template={defaults.header_layout}
            disabled={!editable}
            onSelect={(v) => set({ header_layout: v as Theme['header_layout'] })}
          />
          <LayoutGroup
            label="Footer"
            kind="footer"
            options={vocabulary.footer_layouts}
            value={resolved.footer_layout}
            template={defaults.footer_layout}
            disabled={!editable}
            onSelect={(v) => set({ footer_layout: v as Theme['footer_layout'] })}
          />
        </CardBody>
      </Card>

      {/* ── 3b. one page or pages (C17, FR-I18) ───────────────────────── */}
      {navModes.length > 1 && (
        <Card data-look-card="nav-mode">
          <CardBody className="space-y-4">
            <CardHeading
              title="One page or pages"
              note="How your header menu takes visitors round your site. Give a section a menu label in the page editor to put it in a one-page menu."
            />
            <div role="radiogroup" aria-label="One page or pages" className="grid gap-3 sm:grid-cols-2">
              {navModes.map((mode) => (
                <ChoiceCard
                  key={mode}
                  kind="nav-mode"
                  value={mode}
                  checked={(resolved.nav_mode ?? 'pages') === mode}
                  isTemplate={(defaults.nav_mode ?? 'pages') === mode}
                  disabled={!editable}
                  onSelect={() => set({ nav_mode: mode as Theme['nav_mode'] })}
                >
                  <span className="block text-sm font-medium text-foreground">{NAV_MODES[mode]?.label}</span>
                  <span className="block text-xs text-muted-foreground">{NAV_MODES[mode]?.note}</span>
                </ChoiceCard>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ── 4. logo & browser icon ───────────────────────────────────── */}
      <Card data-look-card="logo">
        <CardBody className="space-y-4">
          <CardHeading
            title="Logo & browser icon"
            note="Your logo is your firm’s one logo — the same on your website, client portal and sign-in page — and it saves as soon as you change it. The browser icon saves with the rest of this page."
          />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-surface-raised p-4">
              {/* ADR-0030 — the firm's one logo, the control *Your firm* shows. */}
              <LogoControl
                value={logo}
                profession={current.profession}
                editable={editable}
                canBrowse={can(SITE, 'can_view')}
                canUpload={editable}
                onChange={saveLogo}
              />
            </div>
            <div className="rounded-xl border border-border bg-surface-raised p-4">
              <ImageField
                label="Browser icon"
                hint="The small square in a browser tab. A square PNG works best."
                value={favicon}
                disabled={!editable}
                onChange={(next) => {
                  setFavicon(next)
                  setNotice(null)
                }}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <EditBar
        dirty={dirty}
        saving={busy}
        editable={editable}
        onSave={() => void save()}
        onDiscard={discard}
        problem={problem}
        previewHref={previewHref}
      />
    </EditPage>
  )
}

/* ── pieces ───────────────────────────────────────────────────────────── */

function CardHeading({ title, note }: { title: string; note: string }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{note}</p>
    </div>
  )
}

/**
 * One colour: a swatch in it (labelled in the ink the renderer will put on it),
 * a picker and its hex, and the template's value beside it.
 *
 * `value` is what the firm has typed (empty = the template's); `resolved` is
 * what the site renders with. The hex box shows the resolved value until the
 * firm types, so it is never blank — but a firm on the template's colour is
 * told so, and one on its own gets *Use the template's* for that colour alone.
 */
function ColourPanel({
  name,
  label,
  value,
  resolved,
  template,
  disabled,
  onChange,
  onReset,
}: {
  name: 'primary' | 'accent'
  label: string
  value: string
  resolved: string
  template: string
  disabled: boolean
  onChange: (next: string) => void
  onReset: () => void
}) {
  const own = value !== ''
  const shown = own ? value : resolved
  const swatch = asColourInput(shown, template)
  const bad = own && !isStorableColour(value)
  return (
    <div data-colour={name} className="space-y-3 rounded-xl border border-border bg-surface-raised p-4">
      <div
        className="flex h-16 items-end justify-between rounded-lg px-3 py-2 ring-1 ring-black/5"
        style={{ backgroundColor: swatch, color: inkOn(swatch) }}
        aria-hidden
      >
        <span className="text-sm font-semibold">{label}</span>
        <span className="font-mono text-xs opacity-80">{swatch}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={swatch}
          disabled={disabled}
          aria-label={`Pick the ${name} colour`}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-background p-1 disabled:cursor-not-allowed"
        />
        <Input
          value={shown}
          disabled={disabled}
          aria-label={`${label} as a hex code`}
          aria-invalid={bad || undefined}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value.trim())}
          className="min-w-0 font-mono"
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span
          className="inline-block size-3 rounded-sm ring-1 ring-black/10"
          style={{ backgroundColor: asColourInput(template, '#000000') }}
          aria-hidden
        />
        {own ? (
          <>
            <span>
              Your template’s is <code className="font-mono">{template}</code>
            </span>
            {!disabled && (
              <button type="button" className="font-medium text-primary hover:underline" onClick={onReset}>
                Use the template’s
              </button>
            )}
          </>
        ) : (
          <span data-template-colour="">Your template’s colour</span>
        )}
      </div>
      {bad && (
        <p className="text-xs text-destructive">A colour looks like #1f3d63 — a hash and six characters.</p>
      )}
    </div>
  )
}

/**
 * A choice drawn as a card: a radio in the accessibility tree, a card to the
 * eye. The check at the corner and the ring are what *chosen* looks like; a
 * *Template’s* badge marks the one the template would use.
 */
function ChoiceCard({
  kind,
  value,
  checked,
  isTemplate,
  disabled,
  onSelect,
  children,
}: {
  kind: string
  value: string
  checked: boolean
  isTemplate: boolean
  disabled: boolean
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-choice={kind}
      data-value={value}
      disabled={disabled}
      onClick={onSelect}
      className={`w-full min-w-0 rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        checked
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border bg-surface-raised hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm'
      } ${disabled ? 'cursor-not-allowed opacity-70 hover:translate-y-0' : ''}`}
    >
      {/* In the flow, not over it: an absolute badge sat on top of a long
          firm name set in a wide face (the 1280 shot, 2026-09-25). */}
      <span className="mb-2 flex h-5 items-center justify-end gap-1.5">
        {isTemplate && <Badge tone="neutral">Template’s</Badge>}
        <span
          className={`flex size-5 items-center justify-center rounded-full ${
            checked ? 'bg-primary text-primary-foreground' : 'border border-border'
          }`}
        >
          {checked && <Check size={12} aria-hidden />}
        </span>
      </span>
      {children}
    </button>
  )
}

function LayoutGroup({
  label,
  kind,
  options,
  value,
  template,
  disabled,
  onSelect,
}: {
  label: string
  kind: 'header' | 'footer'
  options: string[]
  value: string
  template: string
  disabled: boolean
  onSelect: (value: string) => void
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-foreground">{label}</legend>
      <div role="radiogroup" aria-label={`${label} layout`} className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {options.map((option) => (
          <ChoiceCard
            key={option}
            kind={kind}
            value={option}
            checked={value === option}
            isTemplate={template === option}
            disabled={disabled}
            onSelect={() => onSelect(option)}
          >
            <LayoutDiagram layout={option} surface={kind} />
            <span className="mt-3 block text-sm font-medium text-foreground">
              {layoutWords(kind, option).label}
            </span>
            <span className="block text-xs text-muted-foreground">{layoutWords(kind, option).note}</span>
          </ChoiceCard>
        ))}
      </div>
    </fieldset>
  )
}



/** Drop the keys the firm has not set. `theme` carries only their own choices,
 *  which is what makes "back to the template's" a real action. */
function strip(theme: Partial<Theme>): Partial<Theme> {
  const out: Partial<Theme> = {}
  for (const key of THEME_KEYS) {
    const value = theme[key]
    if (value) (out as Record<string, unknown>)[key] = value
  }
  return out
}

function sentence(value: string): string {
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
